/**
 * Naming Convention Utilities for Frontend
 * 
 * Simple utilities to ensure consistent camelCase naming in the frontend.
 */

export const snakeToCamel = (str: string): string => {
  if (!str || !str.includes('_')) return str
  
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

export const camelToSnake = (str: string): string => {
  if (!str) return str
  
  return str.replace(/([A-Z])/g, '_$1').toLowerCase()
}

/**
 * True only for plain objects ({} / Object.create(null)). Class instances such
 * as Firestore Timestamp, Date, GeoPoint, and DocumentReference return false so
 * key transforms leave them intact — rebuilding them would drop their prototype
 * (e.g. a Timestamp would become a bare { seconds, nanoseconds } and lose
 * toDate(), breaking every instanceof Timestamp / instanceof Date consumer).
 */
const isPlainObject = (value: unknown): boolean => {
  if (value === null || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

export const transformKeysToSnake = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj
  
  if (Array.isArray(obj)) {
    return obj.map(transformKeysToSnake) as T
  }
  
  // Check if this is a Firebase FieldValue object (like serverTimestamp())
  // These objects have a _methodName property and should be preserved as-is
  // Without this check, _methodName would be transformed to _method_name,
  // causing the literal object to be stored instead of the server timestamp
  if ('_methodName' in obj) {
    return obj
  }
  
  const transformed: any = {}
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = camelToSnake(key)
    transformed[snakeKey] = transformKeysToSnake(value)
  }
  
  return transformed
}

export const transformKeysToCamel = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj
  
  if (Array.isArray(obj)) {
    return obj.map(transformKeysToCamel) as T
  }

  // Preserve class instances (Firestore Timestamp, Date, etc.) as-is.
  if (!isPlainObject(obj)) return obj
  
  const transformed: any = {}
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = snakeToCamel(key)
    transformed[camelKey] = transformKeysToCamel(value)
  }
  
  return transformed
}

/**
 * Ensure all object keys follow camelCase convention
 */
export const ensureCamelCase = <T extends Record<string, any>>(obj: T): T => {
  return transformKeysToCamel(obj)
}

/**
 * Common field mappings for reference
 */
export const COMMON_FIELD_MAPPINGS = {
  // Timestamps
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  deletedAt: 'deletedAt',
  expiresAt: 'expiresAt',
  lastViewed: 'lastViewed',
  
  // IDs
  userId: 'userId',
  profileId: 'profileId',
  ownerUid: 'ownerUid',
  customerId: 'customerId',
  subscriptionId: 'subscriptionId',
  
  // Common fields
  firstName: 'firstName',
  lastName: 'lastName',
  birthDate: 'birthDate',
  phoneNumber: 'phoneNumber',
  isActive: 'isActive',
  fileName: 'fileName',
  fileSize: 'fileSize',
  fileType: 'fileType',
} as const 